// Quasar
import { Dialog, Loading, Notify, QSpinnerRings } from 'quasar'

// Capacitor
import { Capacitor } from '@capacitor/core'
import { Geolocation } from '@capacitor/geolocation'

// Plugins
import { BinnacleGeo } from '@/geolocation/binnacleGeo'

// Geolocalizacion
import {
  DEFAULT_MOCK_POLICY,
  VALID_MOCK_POLICIES,
  clearCachedMockPolicy,
  fetchMockPolicy,
  readCachedMockPolicy
} from 'src/geolocation/mock-policy-flag'
import { reportMockDetection, resetMockReportThrottle } from '@/geolocation/mock-location-log'
import { simulatedPosition } from '@/geolocation/mock-simulator'

// i18n
import { i18n } from '@/boot/i18n'

// Components
import GeolocationMockBlockedDialog from '@/components/geolocation/GeolocationMockBlockedDialog.vue'

const POSITION_TIMEOUT = 45000

// Sin entrada para la ubicacion ficticia: ese aviso no es un Notify, es el Dialog persistente de
// notifyMockBlocked. Las razones de permiso las resuelve reasonForGeoError a partir del codigo.
const BLOCK_REASONS = {
  out_of_area: 'geolocation.blocked_out_of_area',
  permission_denied: 'geolocation.permission_denied',
  precise_location_required: 'geolocation.precise_location_required',
  timeout: 'geolocation.timeout',
  unavailable: 'geolocation.blocked_unavailable'
}

const BLOCK_NOTIFY_TIMEOUT = 8000

export default {
  // enforceMock=false lo usan los modulos donde la geolocalizacion es solo una comodidad
  // (revisiones rapidas, caducidades, el diagnostico de GPS del menu): obtienen la posicion para
  // preseleccionar la ubicacion, pero una ubicacion ficticia NO los bloquea ni les muestra aviso.
  // El bloqueo por mock queda reservado a formularios y exhibiciones (enforceMock=true, el default).
  // mockContext ({ module, occurrence_id }) viaja al registro del bloqueo en el back; sin el, se
  // registra como formularios sin ocurrencia.
  async fetchCurrent ({ enforceMock = true, mockContext = {} } = {}) {
    Loading.show({
      message: 'Obteniendo ubicación del Dispositivo...',
      spinner: QSpinnerRings,
      delay: 400,
      spinnerSize: 350,
      backgroundColor: 'white10'
    })
    try {
      const position = Capacitor.isNativePlatform()
        ? await this.readNativePosition()
        : await this.readWebPosition()

      this.updatePosition(position)
      Loading.hide()
      if (enforceMock) this.assertNotMocked(mockContext)

      return position.coords
    } catch (error) {
      Loading.hide()
      throw error
    }
  },
  // El plugin oficial conserva el flujo de permisos (diálogos del sistema); BinnacleGeo solo
  // obtiene el fix, con la bandera isMock que el plugin oficial no expone.
  async readNativePosition () {
    let status = await Geolocation.checkPermissions()
    if (status.location !== 'granted') {
      status = await Geolocation.requestPermissions()
    }

    // Sin mirar el resultado, un permiso negado llegaba al plugin propio y volvia como un rechazo
    // del que el guard no puede deducir que el problema son los permisos. Aqui el codigo ya es
    // exacto: BINGEO-01 si no hay ninguno, BINGEO-05 si solo se concedio el aproximado.
    if (status.location !== 'granted') {
      const error = new Error('Location permission not granted')
      error.code = status.coarseLocation === 'granted' ? 'BINGEO-05' : 'BINGEO-01'
      throw error
    }

    const raw = await BinnacleGeo.getCurrentPosition({ timeout: POSITION_TIMEOUT })

    return {
      coords: {
        latitude: raw.latitude,
        longitude: raw.longitude,
        accuracy: raw.accuracy,
        altitude: raw.altitude ?? null
      },
      timestamp: raw.timestamp,
      isMock: raw.isMock ?? null,
      provider: raw.provider ?? null
    }
  },
  // En web no existe la bandera: isMock queda null y nunca bloquea (salvo el simulador de dev).
  async readWebPosition () {
    const simulated = simulatedPosition()
    if (simulated) return simulated

    const raw = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: POSITION_TIMEOUT
    })

    return {
      coords: raw.coords,
      timestamp: raw.timestamp,
      isMock: null,
      provider: null
    }
  },
  assertNotMocked (mockContext = {}) {
    if (!this.shouldBlockForMock) return

    this.notifyMockBlocked(mockContext)

    const error = new Error('Mock location detected')
    error.code = 'BINGEO-04'
    throw error
  },
  // El aviso de ubicacion ficticia es el diseno sellado por diseno (tarjeta blanca + boton
  // "Entendido" en rojo outline): un componente propio en vez del Dialog generico. Sigue persistent,
  // asi que se queda hasta que el usuario lo confirma.
  // Todo bloqueo pasa por aqui (fetchCurrent, guard con posicion cacheada, FormLocation), por eso
  // aqui mismo se registra en el back. Sin await: el aviso no espera a la red.
  notifyMockBlocked (mockContext = {}) {
    Dialog.create({
      component: GeolocationMockBlockedDialog
    })

    reportMockDetection(this.current, mockContext)
  },
  notifyGeoBlocked (reason) {
    Notify.create({
      color: 'negative',
      textColor: 'white',
      icon: 'location_off',
      position: 'bottom',
      multiLine: true,
      message: i18n.global.t(BLOCK_REASONS[reason] || BLOCK_REASONS.unavailable),
      timeout: BLOCK_NOTIFY_TIMEOUT,
      actions: [{ label: i18n.global.t('geolocation.blocked_dismiss'), color: 'white' }]
    })
  },
  // Precedencia: lo que responde el server > la cache de ESTA organizacion > el default
  // (apagado). Heredar la cache de otra organizacion seria justo el agujero que el sello de
  // organizacion vino a cerrar.
  //
  // La cache de esta organizacion se aplica EN EL ACTO, antes de esperar al server: el store arranca
  // en el default (apagado) y, mientras la peticion esta en vuelo —hasta el timeout si no hay red—,
  // un deep link o una notificacion que abre un formulario pasaria sin validar aunque el
  // dispositivo ya sepa que su organizacion bloquea.
  async refreshMockPolicy (orgId) {
    const cached = readCachedMockPolicy(orgId)
    if (cached) this.setMockPolicy(cached)

    const policy = await fetchMockPolicy(orgId)
    const effective = policy || cached || DEFAULT_MOCK_POLICY

    this.setMockPolicy(effective)

    return effective
  },
  setMockPolicy (policy) {
    this.mockPolicy = VALID_MOCK_POLICIES.includes(policy) ? policy : DEFAULT_MOCK_POLICY
  },
  forgetMockPolicy () {
    clearCachedMockPolicy()
    resetMockReportThrottle()
    this.mockPolicy = DEFAULT_MOCK_POLICY
  },
  updatePosition (position) {
    this.current = position
    this.status = 'success'
  }
}
